import { OnlineOrderBranchSettingFragment } from '@client/graphql/online-order-branch-setting-fragment';
import { getFragmentData } from '@client/graphql/types';
import { OnlineOrderPaymentMethod } from '@client/graphql/types/graphql.ts';
import ApplicationErrorView from '@client/module/error/application-error-view';
import { useTranslate } from '@tolgee/react';
import { useCompanyId } from 'core/src/company/focused-company-context';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PrimaryButton from 'ui/src/button/primary-button.tsx';
import CardContent from 'ui/src/card/card-content.tsx';
import CardFooter from 'ui/src/card/card-footer.tsx';
import CardHeader from 'ui/src/card/card-header.tsx';
import Card from 'ui/src/card/card.tsx';
import Center from 'ui/src/center';
import FormItem from 'ui/src/form/form-item.tsx';
import FormLayout from 'ui/src/form/form-layout.tsx';
import MenuSelectionInput from 'ui/src/form/input/menu-selection-input.tsx';
import ToggleInput from 'ui/src/form/input/toggle-input.tsx';
import { useNotificationCenter } from 'ui/src/notification/notification-center.ts';
import Spinner from 'ui/src/spinner';
import useStripeApiKeys from '../../stripe/stripe-api-key/logic/use-stripe-api-keys.ts';
import { useFocusedBranchContext } from '../../table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import useOnlineOrderBranchSetting from './logic/use-online-order-branch-setting.ts';
import useUpdateOnlineOrderBranchSetting from './logic/use-update-online-order-branch-setting.ts';

interface PaymentSetting {
  paymentMethods: OnlineOrderPaymentMethod[];
  stripeAPIKeyId: string;
}

const EMPTY_STRIPE_API_ID = 'empty';

export default function OnlineOrderStripeSetting() {
  // Params
  const branchProvider = useFocusedBranchContext();
  const branchID = branchProvider.branch?.id ?? '';
  const companyId = useCompanyId();
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  // Hooks
  const { data, loading, error, refetch } = useOnlineOrderBranchSetting(branchID);
  const { data: stripeAPIKeysData, loading: stripeAPIKeysLoading } = useStripeApiKeys(companyId);
  const [updateOnlineOrderBranchSetting, { loading: updateLoading }] = useUpdateOnlineOrderBranchSetting();

  //State

  const form = useForm<PaymentSetting>({
    defaultValues: {
      paymentMethods: [],
      stripeAPIKeyId: '',
    },
  });

  // Data
  const onlineOrderBranchSettingData = getFragmentData(
    OnlineOrderBranchSettingFragment,
    data?.onlineOrderBranchConfiguration
  );

  // Effects
  useEffect(() => {
    if (onlineOrderBranchSettingData && stripeAPIKeysData?.stripeAPIKeys) {
      // Find the selected stripe API key
      const selectedKey = stripeAPIKeysData.stripeAPIKeys.find(
        (key) => key.id === onlineOrderBranchSettingData.stripeAPIKeyId
      );

      form.reset({
        paymentMethods: onlineOrderBranchSettingData.paymentMethods,
        stripeAPIKeyId: selectedKey?.id,
      });
    }
  }, [form, onlineOrderBranchSettingData, stripeAPIKeysData]);

  // Handlers
  const handleSave = (data: PaymentSetting) => {
    const resolvedPaymentMethod = data.stripeAPIKeyId
      ? data.paymentMethods
      : data.paymentMethods.filter((item) => item !== OnlineOrderPaymentMethod.Stripe);

    updateOnlineOrderBranchSetting({
      variables: {
        branchID,
        input: {
          stripeAPIKeyId: data.stripeAPIKeyId ? data.stripeAPIKeyId : undefined,
          paymentMethods: resolvedPaymentMethod,
        },
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          message: t(
            'online-order.stripe-setting.update.success.message',
            'Successfully updated stripe payment settings'
          ),
          title: t('online-order.stripe-setting.update.success.title', 'Success'),
        });
        void refetch();
      })
      .catch(() => {
        showAlert({
          status: 'error',
          message: t('online-order.stripe-setting.update.error.message', 'Failed to update stripe payment settings'),
          title: t('online-order.stripe-setting.update.error.title', 'Error'),
        });
      });
  };

  // Safety check
  if (loading || stripeAPIKeysLoading) {
    return (
      <Center className="p-8">
        <Spinner />
      </Center>
    );
  }

  if (error) {
    return <ApplicationErrorView error={error} />;
  }

  if (!data?.onlineOrderBranchConfiguration) {
    return null;
  }

  const matchingStripeData = (stripeId: string): { id: string; name: string } => {
    const stripe = stripeAPIKeysData?.stripeAPIKeys.find((item) => item.id == stripeId);

    return {
      id: stripe?.id ?? EMPTY_STRIPE_API_ID,
      name: stripe?.name ?? t('common.empty', 'None'),
    };
  };

  const checkStripePaymentValue = (paymentMethods: OnlineOrderPaymentMethod[]) => {
    return paymentMethods.includes(OnlineOrderPaymentMethod.Stripe);
  };

  const setStripePaymentMethod = (
    paymentMethods: OnlineOrderPaymentMethod[],
    cashEnable: boolean,
    onChange: (paymentMethods: OnlineOrderPaymentMethod[]) => void
  ) => {
    const check = paymentMethods.includes(OnlineOrderPaymentMethod.Stripe);

    if (cashEnable && !check) {
      onChange([...paymentMethods, OnlineOrderPaymentMethod.Stripe]);
    }

    if (!cashEnable && check) {
      onChange(paymentMethods.filter((item) => item != OnlineOrderPaymentMethod.Stripe));
    }

    return;
  };

  return (
    <Card>
      <CardHeader title={t('online-order.stripe-setting.title', 'Stripe Payment Settings')} withBackButton={true} backTarget={-1} />
      <CardContent>
        <FormLayout>
          <FormItem title={t('online-order.stripe-setting.stripe-api-key', 'Stripe API Key')} className="max-w-md">
            <Controller
              control={form.control}
              name="stripeAPIKeyId"
              render={({ field: { value, onChange } }) => (
                <MenuSelectionInput
                  title=""
                  data={stripeAPIKeysData?.stripeAPIKeys ?? []}
                  value={matchingStripeData(value)}
                  onChange={(value) => {
                    onChange(value.id);
                  }}
                  build={(e) => ({
                    id: e.id,
                    name: e.name,
                  })}
                />
              )}
            />
          </FormItem>

          {form.watch('stripeAPIKeyId') && (
            <FormItem title={t('online-order.stripe-setting.stripe-tolgee', 'Stripe payment')}>
              <p className="my-2 mb-4 text-sm text-gray-500">
                {t(
                  'online-order.stripe-setting.stripe-api-key-stripe-description',
                  'Select a Stripe API key to use for payment processing'
                )}
              </p>
              <Controller
                control={form.control}
                name="paymentMethods"
                render={({ field: { value, onChange } }) => (
                  <ToggleInput
                    value={checkStripePaymentValue(value)}
                    onChange={(newValue: boolean) => {
                      setStripePaymentMethod(value, newValue, onChange);
                    }}
                  />
                )}
              />
            </FormItem>
          )}
        </FormLayout>
      </CardContent>
      <CardFooter>
        <PrimaryButton
          onClick={() => {
            void form.handleSubmit(handleSave)();
          }}
          loading={updateLoading}
          disabled={updateLoading}
        >
          {t('common.button.save', 'Save')}
        </PrimaryButton>
      </CardFooter>
    </Card>
  );
}
